import { Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { version } from 'src/environments/version';

import { getBrowserSessionId } from './browser-session-id';
import { FrontendErrorReporter } from './frontend-error.reporter';
import { FrontendErrorStore } from './frontend-error.store';
import { BrowserFrontendErrorTransport } from './frontend-error.transport';

import type { FrontendErrorContext, FrontendErrorRecord } from './frontend-error.model';

function createBrowserReporter(
  onEntryCaptured?: (entry: FrontendErrorRecord) => void,
  onRecoveryRequired?: (reason: 'chunk-load' | 'i18n-bootstrap-failure') => void
): FrontendErrorReporter {
  return new FrontendErrorReporter({
    store: new FrontendErrorStore(),
    transport: new BrowserFrontendErrorTransport(),
    getLocation: () => ({
      href: globalThis.location?.href,
      pathname: globalThis.location?.pathname
    }),
    getUserAgent: () => globalThis.navigator?.userAgent,
    getAppContext: () => ({
      appVersion: version.hash,
      appVersionDate: version.date,
      environment: environment.ENV_NAME,
      online: globalThis.navigator?.onLine,
      serviceWorkerControlled: !!globalThis.navigator?.serviceWorker?.controller,
      navigationType: getNavigationType(),
      browserSessionId: getBrowserSessionId()
    }),
    onEntryCaptured,
    onRecoveryRequired
  });
}

function getNavigationType(): string | undefined {
  const navigation = globalThis.performance?.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
  return navigation?.type;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandlerService {
  readonly entries = signal<FrontendErrorRecord[]>([]);
  readonly recoveryDialog = signal<{ reason: 'chunk-load' | 'i18n-bootstrap-failure' } | null>(null);
  readonly browserSessionId = getBrowserSessionId();

  private readonly reporter = createBrowserReporter(
    () => this.syncEntries(),
    (reason) => this.showRecoveryDialog(reason)
  );
  private initialized = false;

  constructor() {
    this.syncEntries();
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    if (typeof window !== 'undefined') {
      this.reporter.installGlobalListeners(window);
    }

    this.reporter.installConsoleErrorCapture(console);
    if (typeof globalThis.fetch === 'function') {
      this.reporter.installFetchCapture(globalThis.fetch, (patchedFetch) => {
        globalThis.fetch = patchedFetch;
      });
    }
    this.syncEntries();
  }

  capture(error: unknown, context: FrontendErrorContext): void {
    void this.reporter.capture(error, context).then(() => this.syncEntries());
  }

  clear(): void {
    this.reporter.clear();
    this.syncEntries();
  }

  writeToConsole(...args: unknown[]): void {
    this.reporter.writeToConsole(...args);
  }

  getErrorCount(): number {
    return this.entries().length;
  }

  reloadForRecovery(): void {
    const reason = this.recoveryDialog()?.reason;
    if (reason) {
      this.recordRecovery(reason, 'reload-clicked');
    }
    globalThis.location?.reload();
  }

  dismissRecoveryDialog(): void {
    const reason = this.recoveryDialog()?.reason;
    if (reason) {
      this.recordRecovery(reason, 'dismissed');
    }
    this.recoveryDialog.set(null);
  }

  private showRecoveryDialog(reason: 'chunk-load' | 'i18n-bootstrap-failure'): void {
    if (this.recoveryDialog()) {
      return;
    }

    this.recoveryDialog.set({ reason });
    this.recordRecovery(reason, 'dialog-shown');
  }

  private recordRecovery(
    recoveryReason: 'chunk-load' | 'i18n-bootstrap-failure',
    recoveryAction: 'dialog-shown' | 'reload-clicked' | 'dismissed'
  ): void {
    this.capture(new Error(`Frontend recovery ${recoveryAction}`), {
      source: 'frontend-recovery',
      logLevel: 'info',
      recoveryReason,
      recoveryAction
    });
  }

  private syncEntries(): void {
    this.entries.set(this.reporter.getEntries());
  }
}

export function reportBootstrapError(error: unknown): void {
  const reporter = createBrowserReporter();
  void reporter.capture(error, { source: 'bootstrap' });
  reporter.writeToConsole(error);
}
